import { useState } from 'react';
import avatarCrew from '../assets/landing/how-it-works/avatar-crew.png';
import avatarJess from '../assets/landing/how-it-works/avatar-jess.png';
import avatarMaya from '../assets/landing/how-it-works/avatar-maya.png';
import avatarNoah from '../assets/landing/how-it-works/avatar-noah.png';
import avatarParents from '../assets/landing/how-it-works/avatar-parents.png';
import leafLeft from '../assets/landing/how-it-works/leaf-left.png';
import leafRight from '../assets/landing/how-it-works/leaf-right.png';

const steps = [
  ['1', 'Pick who you want to see', 'Choose one person, a group, family, friends, or a recurring crew.'],
  ['2', 'Send one simple invite', 'CatchUp sends a no-download link where people choose what works.'],
  ['3', 'CatchUp finds the best time', 'The app compares responses and helps confirm the plan.'],
] as const;

const tabs = ['People', 'Groups', 'Recurring'] as const;

const people = [
  ['Maya', avatarMaya],
  ['Noah', avatarNoah],
  ['Jess', avatarJess],
  ['The Crew', avatarCrew],
  ['Parents', avatarParents],
] as const;

const benefits = [
  ['✓', 'No app for guests', 'Everyone can respond from the link.'],
  ['□', 'Private responses', 'Availability is private until the plan is set.'],
  ['♥', 'More quality time', 'Less planning friction, more real connection.'],
] as const;

export function HowItWorksSection() {
  const [activeTab, setActiveTab] = useState<(typeof tabs)[number]>('People');
  const [selectedPeople, setSelectedPeople] = useState<Set<string>>(new Set());
  const [copyLabel, setCopyLabel] = useState('Copy link');
  const [status, setStatus] = useState('People tab selected');

  const togglePerson = (name: string) => {
    setSelectedPeople((current) => {
      const next = new Set(current);
      if (next.has(name)) {
        next.delete(name);
        setStatus(`${name} removed`);
      } else {
        next.add(name);
        setStatus(`${name} selected`);
      }
      return next;
    });
  };

  return (
    <section
      aria-labelledby="how-it-works-title"
      className="relative isolate overflow-hidden bg-[#FBFCFF] px-6 py-12 text-center md:px-8 md:pb-16 md:pt-10"
      id="how-it-works"
    >
      <img
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute left-[max(1rem,calc(50%-724px))] top-[540px] z-0 hidden w-[90px] opacity-90 lg:block"
        src={leafLeft}
      />
      <img
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute right-[max(1rem,calc(50%-724px))] top-[540px] z-0 hidden w-[100px] opacity-90 lg:block"
        src={leafRight}
      />

      <div className="relative z-10 mx-auto max-w-[1332px]">
        <p className="inline-flex min-h-[40px] items-center justify-center gap-2.5 rounded-full bg-[#EDE8FF]/90 px-5 text-[16px] font-bold text-[#3926E8]">
          <span aria-hidden="true" className="text-[#FF6B5F]">☼</span>
          How it works
        </p>
        <h2
          className="mx-auto mt-5 font-serif text-[44px] font-black leading-[0.98] tracking-normal text-[#080D3D] sm:text-[60px] lg:whitespace-nowrap lg:text-[72px]"
          id="how-it-works-title"
        >
          How CatchUp <span className="text-[#F5665F]">works</span>
        </h2>
        <p className="mx-auto mt-5 max-w-[680px] text-lg font-medium leading-[1.45] text-[#404B75] sm:text-[20px]">
          Three simple steps to better plans and stronger connections.
        </p>

        <div className="relative mx-auto mt-8 grid max-w-[980px] gap-5 text-center md:grid-cols-3 md:gap-12 before:absolute before:left-[12%] before:right-[12%] before:top-6 before:hidden before:border-t-2 before:border-dashed before:border-[#B9A8FF] md:before:block">
          {steps.map(([number, title, body]) => (
            <article className="relative z-10 grid justify-items-center" key={title}>
              <div className="grid h-11 w-11 place-items-center rounded-full bg-gradient-to-br from-[#9678FF] to-[#4A28F0] text-xl font-black text-white shadow-[0_12px_24px_rgba(57,38,232,0.24)]">
                {number}
              </div>
              <h3 className="mt-4 min-h-[29px] text-[19px] font-black leading-tight text-[#080D3D]">
                {title}
              </h3>
              <p className="mt-2 max-w-[290px] text-[16px] font-medium leading-snug text-[#404B75]">
                {body}
              </p>
            </article>
          ))}
        </div>

        <div className="mt-6 grid items-stretch gap-8 lg:grid-cols-3 lg:gap-12">
          <section
            aria-label="Pick people card"
            className="rounded-[18px] bg-white p-5 text-left shadow-[0_24px_64px_rgba(44,32,112,0.10)] ring-1 ring-[#E7E2F6] lg:min-h-[410px]"
          >
            <div aria-label="Invite type" className="grid grid-cols-3 gap-2 border-b border-[#E1E5F2] pb-2" role="tablist">
              {tabs.map((tab) => (
                <button
                  aria-selected={activeTab === tab}
                  className={`min-h-11 rounded-[10px] px-2 text-sm font-black transition ${activeTab === tab ? 'bg-[#F1EDFF] text-[#3926E8] shadow-[inset_0_-2px_0_#3926E8]' : 'text-[#64708F] hover:text-[#3926E8]'}`}
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    setStatus(`${tab} tab selected`);
                  }}
                  role="tab"
                  type="button"
                >
                  {tab}
                </button>
              ))}
            </div>

            <label className="mt-5 flex min-h-12 items-center gap-3 rounded-[12px] border border-[#E1E5F2] bg-[#FBFCFF] px-4 text-[#6B748F]">
              <span aria-hidden="true">⌕</span>
              <input
                className="min-h-11 min-w-0 flex-1 bg-transparent text-base font-medium outline-none placeholder:text-[#8B93AA]"
                placeholder="Search people or groups"
                type="search"
              />
            </label>

            <p className="mt-6 text-[15px] font-medium text-[#404B75]">Frequently with</p>
            <div aria-label="Frequent invitees" className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:flex xl:justify-between xl:gap-2">
              {people.map(([name, avatar]) => {
                const selected = selectedPeople.has(name);
                return (
                  <button
                    aria-pressed={selected}
                    className={`flex min-h-[82px] min-w-11 flex-col items-center justify-center gap-2 rounded-[16px] border bg-white px-3 py-3 text-sm font-bold text-[#080D3D] shadow-sm transition xl:min-h-[78px] xl:border-transparent xl:px-1 ${selected ? 'border-[#3926E8] ring-4 ring-[#3926E8]/15' : 'border-[#E4E7F0] hover:border-[#BDB5F8]'}`}
                    key={name}
                    onClick={() => togglePerson(name)}
                    type="button"
                  >
                    <img alt={`${name} avatar`} className="h-[54px] w-[54px] rounded-full object-cover xl:h-[52px] xl:w-[52px]" src={avatar} />
                    <span className="max-w-[66px] truncate">{name}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-7 flex items-start gap-4 rounded-[14px] bg-[#F1EDFF] p-5">
              <span aria-hidden="true" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white text-xl text-[#3926E8]">
                ◎
              </span>
              <div>
                <h3 className="text-lg font-black text-[#080D3D]">Anyone can join</h3>
                <p className="mt-1 text-sm font-medium text-[#404B75]">Guests do not need an account.</p>
              </div>
            </div>
          </section>

          <section
            aria-label="Send invite card"
            className="rounded-[18px] bg-[#FFF8F4] p-7 text-center shadow-[0_24px_64px_rgba(44,32,112,0.10)] ring-1 ring-[#F4DFD2] lg:min-h-[410px]"
          >
            <div aria-hidden="true" className="mx-auto grid h-[70px] w-[70px] place-items-center rounded-full bg-white text-4xl text-[#F5665F] shadow-[0_18px_34px_rgba(245,102,95,0.16)]">
              ✈
            </div>
            <h3 className="mt-4 text-[20px] font-black text-[#080D3D]">Invite sent!</h3>
            <p className="mx-auto mt-2 max-w-[260px] text-[15px] font-medium leading-snug text-[#404B75]">
              We will collect availability and let you know the best time.
            </p>

            <div className="mt-5 rounded-[14px] bg-white p-4 text-left shadow-[0_18px_40px_rgba(100,72,45,0.08)]">
              <div className="flex flex-col gap-3 rounded-[12px] border border-[#ECE5DD] bg-[#FBFCFF] p-3 sm:flex-row sm:items-center">
                <span className="min-w-0 flex-1 truncate text-sm font-bold text-[#3926E8]">catchup.app/invite/brunch</span>
                <button
                  className="min-h-11 rounded-[10px] bg-[#F1EDFF] px-4 text-sm font-black text-[#3926E8] hover:bg-[#E7DFFF] focus:outline-none focus:ring-4 focus:ring-[#3926E8]/20"
                  onClick={() => {
                    setCopyLabel('Copied');
                    setStatus('Invite link copied');
                  }}
                  type="button"
                >
                  {copyLabel}
                </button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm font-bold text-[#080D3D] xl:grid-cols-4">
                {['Message', 'WhatsApp', 'Email', 'More'].map((option) => (
                  <button
                    className="min-h-11 rounded-[14px] border border-[#E8E5EF] bg-white px-3 hover:border-[#BDB5F8]"
                    key={option}
                    type="button"
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 flex items-start gap-4 rounded-[14px] bg-[#F1EDFF] p-4 text-left">
              <span aria-hidden="true" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white text-xl text-[#3926E8]">
                ⛓
              </span>
              <div>
                <h3 className="text-lg font-black text-[#080D3D]">Guests tap the link</h3>
                <p className="mt-1 text-sm font-medium text-[#404B75]">They pick what works, super easy.</p>
              </div>
            </div>
          </section>

          <section
            aria-label="Best time found card"
            className="rounded-[18px] bg-white p-6 text-center shadow-[0_24px_64px_rgba(44,32,112,0.10)] ring-1 ring-[#E7E2F6] lg:min-h-[410px]"
          >
            <p className="mx-auto inline-flex min-h-10 items-center gap-2 rounded-full bg-[#EDE8FF] px-4 text-[20px] font-black text-[#10A243]">
              <span aria-hidden="true">✓</span>
              Best time found!
            </p>
            <h3 className="mx-auto mt-5 max-w-[320px] text-[20px] font-black leading-tight text-[#080D3D]">
              Saturday, May 24 at 11:00 AM
            </h3>
            <p className="mt-3 text-[16px] font-medium text-[#404B75]">Works for 5 of 6 people</p>

            <div aria-label="People available" className="mt-5 flex justify-center -space-x-3">
              {people.map(([name, avatar]) => (
                <img
                  alt={`${name} available`}
                  className="h-11 w-11 rounded-full border-[3px] border-white object-cover shadow-sm"
                  key={name}
                  src={avatar}
                />
              ))}
              <span className="grid h-11 w-11 place-items-center rounded-full border-[3px] border-white bg-[#F1EDFF] text-sm font-medium text-[#404B75] shadow-sm">+1</span>
            </div>

            <div aria-hidden="true" className="mt-8 grid grid-cols-5 text-xs font-medium text-[#404B75]">
              <span>9 AM</span>
              <span>10 AM</span>
              <span className="text-[#10A243]">11 AM</span>
              <span>12 PM</span>
              <span>1 PM</span>
            </div>
            <div aria-hidden="true" className="relative mt-3 h-5 rounded-full bg-[#EEEAFB]">
              <span className="absolute left-[42%] top-1/2 h-8 w-[22%] -translate-y-1/2 rounded-[8px] bg-[#9BE0B1] shadow-[0_10px_20px_rgba(16,162,67,0.16)]" />
              <span className="absolute left-[53%] top-[-18px] h-[56px] w-px bg-[#10A243]" />
            </div>

            <button
              className="mt-9 min-h-14 w-full rounded-[12px] bg-[#3926E8] px-5 text-base font-medium text-white shadow-[0_16px_30px_rgba(57,38,232,0.24)] hover:bg-[#2F20C8] focus:outline-none focus:ring-4 focus:ring-[#3926E8]/20"
              onClick={() => setStatus('Plan confirmed')}
              type="button"
            >
              Confirm plan
            </button>
            <button
              className="mt-4 min-h-11 text-base font-medium text-[#404B75] underline-offset-4 hover:underline"
              onClick={() => setStatus('Time picker opened')}
              type="button"
            >
              Choose a different time
            </button>
          </section>
        </div>

        <div aria-label="CatchUp benefits" className="mt-8 grid gap-0 overflow-hidden rounded-[20px] bg-white text-left shadow-[0_18px_48px_rgba(44,32,112,0.07)] ring-1 ring-[#E7E2F6] md:grid-cols-3">
          {benefits.map(([icon, title, body]) => (
            <article className="flex items-start gap-4 p-6 md:border-l md:border-[#D8DDEA] md:first:border-l-0" key={title}>
              <span aria-hidden="true" className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#F1EDFF] text-xl font-black text-[#3926E8]">
                {icon}
              </span>
              <div>
                <h3 className="text-xl font-black text-[#080D3D]">{title}</h3>
                <p className="mt-1 text-base font-medium leading-snug text-[#404B75]">{body}</p>
              </div>
            </article>
          ))}
        </div>

        <p className="sr-only" role="status">
          {status}
        </p>
      </div>
    </section>
  );
}
